import { Fragment, useState, type CSSProperties } from "react";
import { Loader, Pagination, Select } from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import { CustomerOperations, FlowState, OperationEvent, OperationFlow, OperationsFilter } from "./types";
import { FilterChips, type FilterChip } from "../FilterChips";
import { useIsNarrow } from "../useIsNarrow";
import { Duration, formatDuration } from "../Duration";
import { currentLocale, parseUtcDate } from "../../../../utils/date-utils";
import { useFlowEvents } from "./useCustomerOperations";
import { errorMessage } from "../Toast";

// Inline styles (not Tailwind): these views ship inside the robotics bundle,
// which does NOT include Tailwind — only Mantine (host-provided) + inline CSS.
const c = {
  slate400: "#94a3b8",
  slate500: "#64748b",
  slate700: "#334155",
  slate900: "#0f172a",
  border: "#e2e8f0",
  white: "#ffffff",
  subtle: "#f8fafc",
  orangeText: "#9a3412",
};

const STATE_STYLE: Record<FlowState, { rowBg: string; accent: string; pillBg: string; pillText: string }> = {
  Active: { rowBg: "#f0fdf4", accent: "#22c55e", pillBg: "#dcfce7", pillText: "#166534" },
  Stalled: { rowBg: "#fffbeb", accent: "#f59e0b", pillBg: "#fef3c7", pillText: "#92400e" },
  Malfunction: { rowBg: "#fff7ed", accent: "#f97316", pillBg: "#ffedd5", pillText: "#9a3412" },
  Finished: { rowBg: "#f8fafc", accent: "#cbd5e1", pillBg: "#f1f5f9", pillText: "#64748b" },
};

const OUTCOME_LABELS: Record<string, string> = {
  Completed: "Completed",
  Failed: "Failed",
  Cancelled: "Cancelled",
};

const STEP_NAMES: Record<string, string> = {
  Reservation: "Reservation",
  Retrieval: "Retrieval",
  WeightCheck: "Weight check",
  Storage: "Return",
  DoorOpen: "Door open",
  DockStatusCheck: "Dock check",
  StorageMaintenance: "Storage maintenance",
  Maintenance: "Maintenance",
  PlatformAssignment: "Platform assignment",
};

const STATUS_NAMES: Record<string, string> = {
  InProgress: "In progress",
  AtDock: "At dock",
};

// Values are days; the backend caps the window at 90.
const PERIOD_OPTIONS = [
  { value: "1", label: "Last 24 hours" },
  { value: "3", label: "Last 3 days" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last month" },
  { value: "90", label: "Last 3 months" },
] as const;
export const PERIODS = PERIOD_OPTIONS.map((o) => o.value);
export type Period = (typeof PERIOD_OPTIONS)[number]["value"];

interface CustomerOperationsViewProps {
  data?: CustomerOperations;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  locationId: number;
  locationLabel?: string;
  filter: OperationsFilter;
  onFilterChange: (filter: OperationsFilter) => void;
  period: Period;
  onPeriodChange: (period: Period) => void;
  page: number;
  onPageChange: (page: number) => void;
}

export function CustomerOperationsView({
  data,
  isLoading,
  isError,
  error,
  locationId,
  locationLabel,
  filter,
  onFilterChange,
  period,
  onPeriodChange,
  page,
  onPageChange,
}: CustomerOperationsViewProps) {
  const narrow = useIsNarrow();

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
        <Loader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#991b1b", marginBottom: 8 }}>Error Loading Operations</div>
        <div style={{ color: "#b91c1c" }}>{errorMessage(error)}</div>
      </div>
    );
  }

  const chips: FilterChip[] = [
    { value: "All", label: "All", count: data.total },
    { value: "Active", label: "Active", count: data.activeCount, colors: { bg: "#f0fdf4", border: "#86efac", text: "#166534" } },
    { value: "Stalled", label: "Stalled", count: data.stalledCount, colors: { bg: "#fffbeb", border: "#fcd34d", text: "#b45309" } },
  ];
  const filteredTotal = filter === "Active" ? data.activeCount : filter === "Stalled" ? data.stalledCount : data.total;
  const firstRow = (data.page - 1) * data.pageSize + 1;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: narrow ? 12 : 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c.slate400, marginBottom: 4 }}>
          Customer Operations — {locationLabel ?? "—"}
        </div>
        {!narrow && (
          <div style={{ color: c.slate500 }}>
            Every retrieval, weight check and return, newest activity first. Operations taking longer than{" "}
            {formatDuration(data.targetMinutes)} are flagged as stalled. Click a row to see each step.
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: narrow ? 16 : 20 }}>
        <FilterChips chips={chips} active={filter} onChange={(v) => onFilterChange(v as OperationsFilter)} compact={narrow} />
        <div style={{ marginLeft: "auto" }}>
          <Select
            value={period}
            onChange={(v) => v && onPeriodChange(v as Period)}
            data={PERIOD_OPTIONS.map(({ value, label }) => ({ value, label }))}
            allowDeselect={false}
            w={narrow ? 150 : 170}
            leftSection={<IconCalendar size={16} />}
            aria-label="Period"
          />
        </div>
      </div>

      {data.items.length === 0 ? (
        <div style={{ background: c.white, border: `1px solid ${c.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: c.slate500 }}>
          {filter === "Active"
            ? "No active operations right now."
            : filter === "Stalled"
              ? "Nothing is stalled right now."
              : "No operations in this period."}
        </div>
      ) : (
        <>
          <div style={{ overflowX: "auto", background: c.white, border: `1px solid ${c.border}`, borderRadius: 12 }}>
            <table style={{ width: "100%", minWidth: 820, borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: c.subtle, borderBottom: `1px solid ${c.border}` }}>
                  {["Status", "Customer", "Unit", "Platform", "Last step", "Started", "Time", ""].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((flow) => (
                  <FlowRow key={flow.flowId} flow={flow} locationId={locationId} />
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 16 }}>
            <div style={{ fontSize: 13, color: c.slate500 }}>
              {firstRow}–{firstRow + data.items.length - 1} of {filteredTotal}
            </div>
            {data.pageCount > 1 && <Pagination total={data.pageCount}value={page} onChange={onPageChange} size="sm" siblings={narrow ? 0 : 1} />}
          </div>
        </>
      )}
    </div>
  );
}

const th: CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: c.slate500,
  whiteSpace: "nowrap",
};

const td: CSSProperties = { padding: "10px 12px", verticalAlign: "middle", whiteSpace: "nowrap" };

function FlowRow({ flow, locationId }: { flow: OperationFlow; locationId: number }) {
  const [expanded, setExpanded] = useState(false);
  const style = STATE_STYLE[flow.state] ?? STATE_STYLE.Active;
  const finished = flow.state === "Finished";
  const durationMinutes =
    flow.startedAt && flow.lastActivityAt
      ? (parseUtcDate(flow.lastActivityAt).getTime() - parseUtcDate(flow.startedAt).getTime()) / 60000
      : 0;
  const textColor = finished ? c.slate500 : c.slate900;

  return (
    <>
      <tr
        onClick={() => setExpanded((e) => !e)}
        style={{ background: style.rowBg, borderBottom: `1px solid ${c.border}`, cursor: "pointer", color: textColor }}
      >
        <td style={{ ...td, borderLeft: `4px solid ${style.accent}` }}>
          <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: style.pillBg, color: style.pillText }}>
            {finished ? OUTCOME_LABELS[flow.outcome] ?? "Finished" : flow.state}
          </span>
        </td>
        <td style={{ ...td, fontWeight: 600 }}>{flow.customerName ?? "—"}</td>
        <td style={td}>{flow.unitNumber ?? "—"}</td>
        <td style={td}>{flow.platformNumber ?? "—"}</td>
        <td style={td}>
          {stepLabel(flow.lastOperationType, flow.lastStatus)}
          {flow.errorCount > 0 && (
            <span style={{ marginLeft: 6, color: c.orangeText, fontWeight: 600 }}>
              ⚠ {flow.errorCount}
            </span>
          )}
        </td>
        <td style={td}>{flow.startedAt ? formatTime(flow.startedAt) : "—"}</td>
        <td style={td}>
          {finished ? (
            formatDuration(durationMinutes)
          ) : (
            <span style={{ fontWeight: 600 }}>
              waiting <Duration minutes={flow.waitMinutes} />
            </span>
          )}
        </td>
        <td style={{ ...td, color: c.slate400, width: 24 }}>
          <span style={{ display: "inline-block", transform: expanded ? "rotate(90deg)" : undefined, transition: "transform 120ms" }}>›</span>
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: c.white, borderBottom: `1px solid ${c.border}` }}>
          <td colSpan={8} style={{ padding: "12px 16px 14px 28px" }}>
            <FlowTimeline locationId={locationId} flow={flow} />
          </td>
        </tr>
      )}
    </>
  );
}

function FlowTimeline({ locationId, flow }: { locationId: number; flow: OperationFlow }) {
  const { data: events, isLoading, isError } = useFlowEvents(locationId, flow.flowId, flow.lastActivityAt);

  if (isLoading) return <Loader size="sm" />;
  if (isError || !events) return <div style={{ color: "#b91c1c", fontSize: 13 }}>Could not load the steps of this operation.</div>;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 16, rowGap: 4, fontSize: 13 }}>
      {events.map((e: OperationEvent, i) => (
        <Fragment key={i}>
          <span style={{ color: c.slate500, fontVariantNumeric: "tabular-nums" }}>{formatTime(e.occurredAt, true)}</span>
          <span style={{ color: e.status === "Failed" ? c.orangeText : c.slate700, whiteSpace: "normal" }}>
            {stepLabel(e.operationType, e.status)}
            {e.isAdmin && (
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: c.slate500, background: c.white, border: `1px solid ${c.border}`, borderRadius: 4, padding: "0 5px" }}>
                Manager
              </span>
            )}
            {e.resolved && <span style={{ color: c.slate500 }}> · resolved by platform reset</span>}
            {e.errorMessage && <span style={{ color: c.orangeText }}> — {e.errorMessage}</span>}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

function stepLabel(operationType: string, status: string) {
  return `${STEP_NAMES[operationType] ?? operationType} ${(STATUS_NAMES[status] ?? status).toLowerCase()}`;
}

function formatTime(iso: string, withSeconds = false) {
  const date = parseUtcDate(iso);
  const today = date.toDateString() === new Date().toDateString();
  return date.toLocaleString(currentLocale(), {
    ...(today ? {} : { day: "2-digit", month: "2-digit" }),
    hour: "2-digit",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
    hour12: false,
  });
}
